import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { InvoicingService, InvoicingSettings } from '../services/invoicing.service';
import { ComplexService } from '../services/complex.service';

/**
 * A Romanian fiscal code is prefixed with RO exactly when the company is registered for VAT —
 * that prefix IS the registration, which is why it can be compared against the declaration.
 */
const VAT_REGISTERED_PREFIX = 'RO';

/**
 * The invoicing settings, shared by the four cards on the Settings tab.
 *
 * The tab used to be one component, so one `settings` signal served every control on it. Split
 * into cards — connection, recipients, tax status, wording — that state has to live somewhere
 * they all see: the master switch on the connection card decides whether the recipients and
 * wording cards are live, and every mutation returns the whole settings object. Four components
 * each holding their own copy would mean four GETs and four chances to disagree about whether
 * invoicing is on.
 *
 * The VAT flag is loaded separately from the billing profile, because it is settable BEFORE a
 * provider is connected — it decides 21% vs 0% on the very first invoice issued — and a billing
 * profile that fails to load must not take the tax card down with it.
 */
@Injectable({ providedIn: 'root' })
export class InvoicingSettingsStore {
  private readonly invoicingService = inject(InvoicingService);
  private readonly complexService = inject(ComplexService);

  readonly settings = signal<InvoicingSettings | null>(null);
  readonly loading = signal(true);
  /** True once a load has completed, successfully or not — distinguishes "no data yet" from "failed". */
  readonly loaded = signal(false);
  readonly error = signal(false);

  readonly vatPayer = signal(false);
  readonly vatCode = signal<string | null>(null);

  /**
   * Connected and switched on — the venue expects a document for each paid booking. A provider
   * error does not change that expectation (those documents sit queued), so it is not part of
   * this one; see isActive for "and it is actually working".
   */
  readonly invoicingOn = computed(() => {
    const s = this.settings();
    return !!s?.isConnected && s.isEnabled;
  });

  /** Connected, switched on, and the provider is not complaining. */
  readonly isActive = computed(() => {
    const s = this.settings();
    return !!s?.isConnected && s.isEnabled && !s.lastError;
  });

  /**
   * Whether players actually receive a nominal invoice today. All three conditions matter: a
   * venue that paused the integration, or never connected one, issues just as few documents as
   * one that opted out of invoicing individuals.
   */
  readonly issuingIndividualInvoices = computed(() => {
    const s = this.settings();
    return !!s?.isConnected && s.isEnabled && s.invoiceIndividuals;
  });

  /**
   * Advisory only — nothing here blocks saving. A Romanian fiscal code carries the RO prefix
   * exactly when the company is VAT-registered, so a declaration that disagrees with it means
   * one of the two is wrong, and both mistakes are expensive: charging VAT the venue may not
   * collect, or issuing without VAT while registered to collect it. We say so and let the
   * venue decide; we cannot know which side is the error.
   */
  readonly vatMismatch = computed<string | null>(() => {
    const code = this.vatCode()?.trim();
    if (!code) return null;               // nothing recorded yet — nothing to disagree with

    const registered = code.toUpperCase().startsWith(VAT_REGISTERED_PREFIX);
    if (this.vatPayer() && !registered) return 'SETTINGS.INVOICING.VAT_MISMATCH_NO_PREFIX';
    if (!this.vatPayer() && registered) return 'SETTINGS.INVOICING.VAT_MISMATCH_HAS_PREFIX';
    return null;
  });

  /** Loads once and caches. Every card calls it on init; only the first one costs a request. */
  async load(): Promise<void> {
    if (this.loaded() && !this.error()) return;
    await this.reload();
  }

  /**
   * Clears cached state. Must run on logout: the store is root-provided, so without this the
   * next venue to sign in on the same tab would see the previous venue's invoicing account.
   */
  reset(): void {
    this.settings.set(null);
    this.loading.set(true);
    this.loaded.set(false);
    this.error.set(false);
    this.vatPayer.set(false);
    this.vatCode.set(null);
  }

  /** Forces a fresh read — after connecting, disconnecting, or a failed connection test. */
  async reload(): Promise<void> {
    this.loading.set(true);
    try {
      const settings = await firstValueFrom(this.invoicingService.getSettings());
      this.settings.set(settings);
      this.error.set(false);
    } catch {
      // Swallowed: callers check `error()`. The cards that can still act without settings —
      // tax status above all — must stay usable when the provider read fails.
      this.error.set(true);
    } finally {
      this.loading.set(false);
      this.loaded.set(true);
    }
  }

  /** Records the settings a mutation returned, so every card re-renders from one answer. */
  apply(settings: InvoicingSettings): void {
    this.settings.set(settings);
    this.error.set(false);
  }

  async loadVatProfile(): Promise<void> {
    try {
      const profile = await firstValueFrom(this.complexService.getBillingProfile());
      this.vatPayer.set(profile.isVatPayer);
      this.vatCode.set(profile.vatCode ?? null);
    } catch {
      // No profile yet — the toggle starts from the backend default (not a VAT payer).
    }
  }

  /**
   * Re-reads the fiscal code alone. It may have been filled in on the billing-profile card
   * since this one loaded, and a mismatch warning drawn from a stale code is worse than none.
   */
  async refreshVatCode(): Promise<void> {
    try {
      const profile = await firstValueFrom(this.complexService.getBillingProfile());
      this.vatCode.set(profile.vatCode ?? null);
    } catch {
      // Leave the last known code; the toggle itself already saved.
    }
  }
}
